import QRCode from 'qrcode';

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

function escapeXml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function finderZone(row, column, count) {
  return (
    (row < 7 && column < 7) ||
    (row < 7 && column >= count - 7) ||
    (row >= count - 7 && column < 7)
  );
}

function moduleMarkup(row, column, style, color, radiusPercent) {
  const x = column;
  const y = row;

  if (style === 'dots') {
    return `<circle cx="${x + 0.5}" cy="${y + 0.5}" r="0.42" fill="${color}"/>`;
  }

  const styleRadius = {
    square: 0,
    rounded: 0.18,
    godly: 0.12,
    'godly-round': 0.28,
    'extra-round': 0.45
  }[style] ?? 0.18;
  const radius = Math.max(
    styleRadius,
    Math.min(Number(radiusPercent || 0) / 100, 0.45)
  );

  return `<rect x="${x + 0.04}" y="${y + 0.04}" width="0.92" height="0.92" rx="${radius}" fill="${color}"/>`;
}

function finderMarkup(x, y, style, color, background, radiusPercent) {
  const radiusBoost = Math.min(Number(radiusPercent || 0) / 100, 0.45);
  const radius =
    style === 'circle'
      ? 3.5
      : style === 'rounded'
        ? Math.max(1.4, radiusBoost * 3.5)
        : radiusBoost * 2.2;
  const innerRadius =
    style === 'circle'
      ? 2.5
      : style === 'rounded'
        ? Math.max(1, radiusBoost * 2.5)
        : radiusBoost * 1.5;
  const centerRadius =
    style === 'circle'
      ? 1.5
      : style === 'rounded'
        ? Math.max(0.7, radiusBoost * 1.5)
        : radiusBoost;

  return [
    `<rect x="${x}" y="${y}" width="7" height="7" rx="${radius}" fill="${color}"/>`,
    `<rect x="${x + 1}" y="${y + 1}" width="5" height="5" rx="${innerRadius}" fill="${background}"/>`,
    `<rect x="${x + 2}" y="${y + 2}" width="3" height="3" rx="${centerRadius}" fill="${color}"/>`
  ].join('');
}

function createStyledSvg(payload, options, logo) {
  const qr = QRCode.create(payload, {
    errorCorrectionLevel: options.errorCorrectionLevel || 'H'
  });
  const count = qr.modules.size;
  const margin = options.margin || 0;
  const dark = escapeXml(options.color?.dark || '#000000');
  const light = escapeXml(options.color?.light || '#ffffff');
  const style = options.moduleStyle || 'square';
  const outerCorner = options.outerCorner || 'square';
  const radius = Number(options.radius) || 0;
  const contents = [];

  if (!options.transparentBackground) {
    contents.push(
      `<rect width="${count + margin * 2}" height="${count + margin * 2}" fill="${light}"/>`
    );
  }

  for (let row = 0; row < count; row += 1) {
    for (let column = 0; column < count; column += 1) {
      if (
        qr.modules.data[row * count + column] &&
        !finderZone(row, column, count)
      ) {
        contents.push(
          moduleMarkup(
            row + margin,
            column + margin,
            style,
            dark,
            radius
          )
        );
      }
    }
  }

  contents.push(
    finderMarkup(margin, margin, outerCorner, dark, light, radius),
    finderMarkup(count - 7 + margin, margin, outerCorner, dark, light, radius),
    finderMarkup(margin, count - 7 + margin, outerCorner, dark, light, radius)
  );

  if (logo) {
    const logoSize = count * 0.18;
    const logoPosition = (count + margin * 2 - logoSize) / 2;
    const safeLogo = escapeXml(logo);
    contents.push(
      `<rect x="${logoPosition - 0.45}" y="${logoPosition - 0.45}" width="${logoSize + 0.9}" height="${logoSize + 0.9}" rx="0.7" fill="#ffffff"/>`,
      `<image href="${safeLogo}" xlink:href="${safeLogo}" x="${logoPosition}" y="${logoPosition}" width="${logoSize}" height="${logoSize}" preserveAspectRatio="xMidYMid meet"/>`
    );
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${options.width || 1024}" height="${options.width || 1024}" viewBox="0 0 ${count + margin * 2} ${count + margin * 2}" shape-rendering="geometricPrecision">${contents.join('')}</svg>`;
}

async function loadSvgImage(svg) {
  const url = URL.createObjectURL(
    new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  );

  try {
    return await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function createQrDataUrl(payload, options, logo) {
  const svg = createStyledSvg(payload, options, logo);
  const image = await loadSvgImage(svg);
  const size = options.width || 1024;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = size;
  canvas.height = size;
  context.clearRect(0, 0, size, size);
  context.drawImage(image, 0, 0, size, size);

  return canvas.toDataURL('image/png');
}

export async function createQrSvg(payload, options, logo) {
  return createStyledSvg(payload, options, logo);
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.download = filename;
  link.href = url;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}